import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { DailyWorkDay } from "@/models/daily-work.model";

/**
 * Shows the hours the database resolved for a day and, when the clock record
 * could not be used as-is, why.
 *
 * A day whose hours are unresolved carries no payout total and is deliberately
 * excluded from payment-dues queries, so without this notice it would simply
 * vanish from the payables list with no explanation.
 */

type PayoutReviewDay = Pick<
  DailyWorkDay,
  "id" | "hours_worked" | "hours_source" | "payout_review" | "max_hours"
>;

const formatHours = (hours: number): string => {
  const rounded = Math.round(hours * 100) / 100;
  return `${rounded} ${rounded === 1 ? "hour" : "hours"}`;
};

const describeReview = (day: PayoutReviewDay): string | null => {
  const hours = day.hours_worked ?? null;
  const scheduled = day.max_hours ?? null;

  switch (day.payout_review) {
    case "missing_hours":
      return hours === null
        ? "No finish time was recorded and this day has no scheduled duration, so its hours cannot be worked out. It stays out of payouts until someone enters them."
        : `No finish time was recorded, so the scheduled ${formatHours(hours)} is proposed. The payout is held until someone approves the hours.`;
    case "invalid_span":
      return hours === null
        ? "The start and finish times are the same and there is no scheduled duration to fall back on. This day stays out of payouts until the times are corrected."
        : `The start and finish times are the same, so the scheduled ${formatHours(hours)} is proposed. The payout is held until someone approves the hours.`;
    case "crossed_midnight":
      return hours !== null
        ? `The finish time is earlier than the start time, so this was read as a shift running past midnight: ${formatHours(hours)}. The payout is held until someone approves the hours.`
        : "The finish time is earlier than the start time, so this was read as a shift running past midnight. The payout is held until someone approves the hours.";
    case "over_scheduled":
      return hours !== null && scheduled !== null
        ? `Worked ${formatHours(hours)} against a scheduled ${formatHours(scheduled)}. The full amount is proposed, but the payout is held until someone approves the hours.`
        : "Worked longer than the scheduled duration. The payout is held until someone approves the hours.";
    default:
      return null;
  }
};

interface PayoutReviewNoticeProps {
  day: PayoutReviewDay;
  className?: string;
  onResolveHours?: (hoursWorked: number) => Promise<void>;
}

export default function PayoutReviewNotice({ day, className, onResolveHours }: PayoutReviewNoticeProps) {
  const message = describeReview(day);
  const hours = day.hours_worked ?? null;
  const [hoursInput, setHoursInput] = useState(hours === null ? "" : String(hours));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setHoursInput(hours === null ? "" : String(hours));
    setSaveError(null);
  }, [day.id, hours]);

  const handleResolve = async (event: FormEvent) => {
    event.preventDefault();
    if (!onResolveHours) return;

    const approvedHours = Number(hoursInput);
    if (!Number.isFinite(approvedHours) || approvedHours <= 0) {
      setSaveError("Enter hours greater than zero.");
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);
      await onResolveHours(approvedHours);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to approve payout hours.");
    } finally {
      setSaving(false);
    }
  };

  // Nothing worth saying about a day that has not been completed yet.
  if (!message && hours === null) return null;

  if (!message) {
    return (
      <div
        data-testid="daily-work-payout-hours-recorded"
        className={cn("flex items-center gap-1.5 text-sm text-muted-foreground", className)}
      >
        <Clock className="h-3.5 w-3.5" />
        <span>
          {formatHours(hours as number)} {day.hours_source === "manual" ? "approved manually" : "recorded"}
        </span>
      </div>
    );
  }

  const hoursUnresolved = hours === null;

  return (
    <div
      data-testid="daily-work-payout-review-notice"
      className={cn(
        "flex items-start gap-2 rounded-md border p-2.5 text-sm",
        hoursUnresolved
          ? "border-red-200 bg-red-50 text-red-900"
          : "border-amber-200 bg-amber-50 text-amber-900",
        className
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="font-medium">{hoursUnresolved ? "Hours need attention" : "Hours need approval"}</p>
        <p>{message}</p>
        {onResolveHours && (
          <form className="space-y-1.5 pt-1" onSubmit={handleResolve}>
            <div className="flex items-center gap-2">
              <Input
                aria-label="Approved payout hours"
                data-testid="daily-work-payout-manual-hours"
                type="number"
                min="0.01"
                step="0.01"
                value={hoursInput}
                onChange={(event) => setHoursInput(event.target.value)}
                className="h-8 bg-background text-foreground"
                disabled={saving}
              />
              <Button
                data-testid="daily-work-payout-approve-hours"
                type="submit"
                size="sm"
                disabled={saving}
              >
                {saving ? "Approving..." : "Approve hours"}
              </Button>
            </div>
            {saveError && <p className="text-xs text-red-700">{saveError}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
